import React, { useState, useEffect, useContext } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Slide from "@mui/material/Slide";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { TransitionProps } from "@mui/material/transitions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useSessionContext } from "context/SessionContext";
import { LanguageContext } from "context";

import {
  getUnreadInfoMessages,
  markInfoMessageAsRead,
  snoozeInfoMessage,
  InfoMessage,
} from "@shared/api/infoMessage";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// -----------------------------------------------------------------------------
// セッション中に「今後表示しない」を付けずに閉じた（=永久既読にしていない）メッセージID。
// リロードまでは再表示しないが、リロード後（新セッション）は再び対象になる。
// -----------------------------------------------------------------------------
const dismissedThisSession = new Set<number>();

function InfoMessageDialog() {
  const { session } = useSessionContext();
  const context = useContext(LanguageContext) as any;
  const isEnglish = context?.language === "en";

  const [messages, setMessages] = useState<InfoMessage[]>([]);
  const [currentMsgIndex, setCurrentMsgIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState<1 | 2 | 3>(1);
  const [isOpen, setIsOpen] = useState(false);

  // 「今後このメッセージを表示しない」チェックボックスの状態（メッセージ単位でリセット）
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!session?.access_token) return;
      try {
        const responseData = await getUnreadInfoMessages(session.access_token);
        const data: any = responseData;
        const messagesArray = Array.isArray(data) ? data : data?.messages;

        if (messagesArray && messagesArray.length > 0) {
          // このセッション中に閉じた（永久既読にしていない）ものは除外する
          const filtered = messagesArray.filter(
            (m: InfoMessage) => !dismissedThisSession.has(m.id)
          );
          if (filtered.length > 0) {
            setMessages(filtered);
            setCurrentMsgIndex(0);
            setCurrentPage(1);
            setDontShowAgain(false);
            setIsOpen(true);
          }
        }
      } catch (error) {
        console.error("お知らせの取得に失敗しました", error);
      }
    };
    fetchMessages();
  }, [session?.access_token]);

  const currentMsg = messages[currentMsgIndex];
  const isDialogVisible = isOpen && !!currentMsg;

  const totalPages = currentMsg?.page3_ja_title ? 3 : currentMsg?.page2_ja_title ? 2 : 1;
  const isLastPage = currentPage === totalPages;

  // once（＝閉じたら自動既読）ではチェックボックスを出さない。
  // cooldown / every のように「また出る」タイプでのみ、永久停止の逃げ道として表示する。
  const policy = currentMsg?.redisplay_policy || "once";
  const showDontShowAgain = policy === "cooldown" || policy === "every";

  const getCurrentPageData = () => {
    if (!currentMsg) return { title: "", image: "", body: "" };

    switch (currentPage) {
      case 3:
        return {
          title: isEnglish && currentMsg.page3_en_title ? currentMsg.page3_en_title : currentMsg.page3_ja_title,
          image: currentMsg.page3_image_url,
          body: isEnglish && currentMsg.page3_en_body_text ? currentMsg.page3_en_body_text : currentMsg.page3_ja_body_text,
        };
      case 2:
        return {
          title: isEnglish && currentMsg.page2_en_title ? currentMsg.page2_en_title : currentMsg.page2_ja_title,
          image: currentMsg.page2_image_url,
          body: isEnglish && currentMsg.page2_en_body_text ? currentMsg.page2_en_body_text : currentMsg.page2_ja_body_text,
        };
      case 1:
      default:
        return {
          title: isEnglish && currentMsg.page1_en_title ? currentMsg.page1_en_title : currentMsg.page1_ja_title,
          image: currentMsg.page1_image_url,
          body: isEnglish && currentMsg.page1_en_body_text ? currentMsg.page1_en_body_text : currentMsg.page1_ja_body_text,
        };
    }
  };

  const pageData = getCurrentPageData();

  /**
   * 現在のメッセージを閉じて次へ進む（または全閉じ）。
   * @param forceMarkRead アクションボタン押下時など、ポリシーに関わらず永久既読化したい場合 true
   */
  const handleMarkAsReadAndNext = async (forceMarkRead = false) => {
    if (session?.access_token && currentMsg) {
      const p = currentMsg.redisplay_policy || "once";

      if (forceMarkRead === true || dontShowAgain) {
        // 永久既読（今後一切表示しない）
        markInfoMessageAsRead(session.access_token, currentMsg.id).catch((err) =>
          console.error("既読処理エラー", err)
        );
      } else if (p === "cooldown") {
        // 一定期間後に再表示。閉じた時刻をサーバに記録し、当セッションは抑制
        snoozeInfoMessage(session.access_token, currentMsg.id).catch((err) =>
          console.error("スヌーズ処理エラー", err)
        );
        dismissedThisSession.add(currentMsg.id);
      } else if (p === "every") {
        // 毎回表示。当セッション中のみ抑制
        dismissedThisSession.add(currentMsg.id);
      } else {
        // once（未指定含む）: 従来通り、閉じたら永久既読
        markInfoMessageAsRead(session.access_token, currentMsg.id).catch((err) =>
          console.error("既読処理エラー", err)
        );
      }
    }

    if (currentMsgIndex < messages.length - 1) {
      setCurrentMsgIndex((prev) => prev + 1);
      setCurrentPage(1);
      setDontShowAgain(false);
    } else {
      setIsOpen(false);
    }
  };

  const handleNextPage = () => {
    if (!isLastPage) setCurrentPage((prev) => (prev + 1) as 1 | 2 | 3);
    else handleMarkAsReadAndNext();
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => (prev - 1) as 1 | 2 | 3);
  };

  const handleCloseDialog = () => {
    handleMarkAsReadAndNext();
  };

  const renderDialogHeader = () => {
    if (!currentMsg) return null;

    let icon = null;
    let title = "";

    switch (currentMsg.type) {
      case "tutorial":
        title = isEnglish ? "User Guide" : "使い方ガイド";
        icon = <span style={{ marginRight: 8, fontSize: "1.5rem" }}>🔰</span>;
        break;
      case "notice":
        title = isEnglish ? "Notice" : "お知らせ";
        icon = <Icon sx={{ mr: 1, color: "warning.main", fontSize: "1.5rem !important" }}>notifications_active</Icon>;
        break;
      case "update":
        title = isEnglish ? "Update Info" : "更新のお知らせ";
        icon = <Icon sx={{ mr: 1, color: "info.main", fontSize: "1.5rem !important" }}>system_update_alt</Icon>;
        break;
      default:
        title = isEnglish ? "Information" : "お知らせ";
        icon = <Icon sx={{ mr: 1, color: "secondary.main", fontSize: "1.5rem !important" }}>info</Icon>;
    }

    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" width="100%" mb={2}>
        {icon}
        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#000000" }}>
          {title}
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <Dialog
      open={isDialogVisible}
      onClose={handleCloseDialog}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: 16, overflow: "hidden" }
      }}
    >
      {currentMsg && (
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              color: "#000000",
              "&:hover": { color: "#333333" },
              "&:focus": { color: "#000000" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>

          {/* 戻るボタン (左) */}
          {currentPage > 1 && (
            <IconButton
              onClick={handlePrevPage}
              sx={{
                position: "absolute",
                top: "50%",
                left: 8,
                transform: "translateY(-50%)",
                zIndex: 10,
                color: "#ffffff",
                backgroundColor: "info.main",
                width: 48,
                height: 48,
                boxShadow: 3,
                "&:hover": { backgroundColor: "info.dark" },
              }}
            >
              <Icon fontSize="large" sx={{ color: "#ffffff" }}>chevron_left</Icon>
            </IconButton>
          )}

          {/* 次へボタン (右) */}
          {!isLastPage && (
            <IconButton
              onClick={handleNextPage}
              sx={{
                position: "absolute",
                top: "50%",
                right: 8,
                transform: "translateY(-50%)",
                zIndex: 10,
                color: "#ffffff",
                backgroundColor: "info.main",
                width: 48,
                height: 48,
                boxShadow: 3,
                "&:hover": { backgroundColor: "info.dark" },
              }}
            >
              <Icon fontSize="large" sx={{ color: "#ffffff" }}>chevron_right</Icon>
            </IconButton>
          )}

          <MDBox display="flex" flexDirection="column" alignItems="center" p={4} textAlign="center">

            {renderDialogHeader()}

            {pageData.image && (
              <MDBox mb={3} width="100%" display="flex" justifyContent="center" mx={-4} p={0}>
                <img
                  src={pageData.image}
                  alt={pageData.title || ""}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "auto",
                    objectFit: "fill", // バナーに合わせて埋める
                    borderRadius: 0, // バナーは角丸なし
                  }}
                />
              </MDBox>
            )}

            <MDTypography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "#000000" }}>
              {pageData.title}
            </MDTypography>
            <MDTypography variant="body2" mb={4} sx={{ whiteSpace: "pre-wrap", color: "#000000" }}>
              {pageData.body}
            </MDTypography>

            {/* ページネーション */}
            {totalPages > 1 && (
              <MDBox display="flex" justifyContent="center" mb={3} gap={1}>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <MDBox
                    key={index}
                    width={10}
                    height={10}
                    borderRadius="50%"
                    bgColor={index + 1 === currentPage ? "info" : "grey-300"}
                    sx={{ transition: "all 0.3s ease" }}
                  />
                ))}
              </MDBox>
            )}

            <MDBox width="100%" display="flex" flexDirection="column" alignItems="center">
              {/* 今後表示しないチェックボックス（cooldown / every のときのみ表示） */}
              {showDontShowAgain && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      size="small"
                      sx={{ color: "info.main", "&.Mui-checked": { color: "info.main" } }}
                    />
                  }
                  label={
                    <MDTypography variant="caption" sx={{ color: "#555555" }}>
                      {isEnglish ? "Don't show this again" : "今後このメッセージを表示しない"}
                    </MDTypography>
                  }
                  sx={{ mb: 1 }}
                />
              )}

              {isLastPage && currentMsg.action_url && (
                <MDBox width="100%" display="flex" justifyContent="center" mb={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    onClick={() => {
                      window.open(currentMsg.action_url, "_blank");
                      // アクションを実行した＝エンゲージしたので、ポリシーに関わらず永久既読
                      handleMarkAsReadAndNext(true);
                    }}
                  >
                    {isEnglish && currentMsg.en_action_text ? currentMsg.en_action_text : currentMsg.ja_action_text}
                  </MDButton>
                </MDBox>
              )}

              <MDBox width="100%" display="flex" justifyContent="center" mt={isLastPage && currentMsg.action_url ? 1 : 4}>
                <MDButton
                  variant="contained"
                  sx={{
                    backgroundColor: "#000000 !important",
                    color: "#ffffff !important",
                    borderRadius: "20px",
                    px: 6,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#000000 !important",
                    },
                  }}
                  onClick={handleCloseDialog}
                >
                  {isEnglish ? "Close" : "閉じる"}
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default InfoMessageDialog;
